import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import BlockIcon from '@mui/icons-material/Block';
import { getApiErrorMessage, postApiJson } from '../../../utils/apiFunction';
import { adalApiFetch } from '../../../../adalConfig';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';

const RejectReceivalForm = ({ receivalId, handleClose, onRejected }) => {
    const Swal = useSwalWrapper();
    const [preview, setPreview] = React.useState(null);
    const [reason, setReason] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        let active = true;
        setLoading(true);
        adalApiFetch(fetch, `${process.env.REACT_APP_BASE_URL}/Receivals/Get/${receivalId}/RejectionPreview`)
            .then(async response => {
                if (!response.ok) {
                    throw new Error(await getApiErrorMessage(response, 'Unable to load receival stock.'));
                }
                return response.json();
            })
            .then(data => {
                if (active) {
                    setPreview(data);
                    setError('');
                }
            })
            .catch(requestError => {
                if (active) {
                    setError(requestError.message || 'Unable to load receival stock.');
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [receivalId]);

    const updateQuantityToKeep = (index, value) => {
        const quantity = value === '' ? '' : Number(value);
        setPreview(current => ({
            ...current,
            stock: current.stock.map((item, itemIndex) => itemIndex === index ? { ...item, quantityToKeep: quantity } : item)
        }));
    };

    const rejectReceival = async () => {
        if (!reason.trim()) {
            setError('Enter a reason for rejecting this receival.');
            return;
        }

        const invalidStock = preview.stock.some(item => {
            const quantity = Number(item.quantityToKeep || 0);
            return quantity < 0 || quantity > item.availableQuantity;
        });
        if (invalidStock) {
            setError('Stock to keep must be between zero and the available quantity.');
            return;
        }

        const result = await Swal.fire({
            title: 'Reject this receival?',
            html: 'This immediately removes the receival and purchase order from normal screens and queues the required MYOB reversal.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Reject receival',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#c62828'
        });
        if (!result.value) {
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            const response = await postApiJson(`/Receivals/Reject/${receivalId}`, {
                reason: reason.trim(),
                stockToKeep: preview.stock.map(item => ({
                    stockItemId: item.stockItemId,
                    warehouseId: item.warehouseId,
                    lotSerialNbr: item.lotSerialNbr,
                    quantityToKeep: Number(item.quantityToKeep || 0)
                }))
            });
            if (!response.ok) {
                throw new Error(await getApiErrorMessage(response, 'Unable to reject the receival.'));
            }

            await Swal.fire({
                title: 'Receival rejected',
                text: 'The MYOB reversal has been queued.',
                icon: 'success'
            });
            onRejected();
        } catch (requestError) {
            setError(requestError.message || 'Unable to reject the receival.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    if (!preview) {
        return <Alert severity="error">{error || 'Unable to load receival stock.'}</Alert>;
    }

    return (
        <Stack spacing={2} sx={{ minWidth: { sm: 560 } }}>
            <Alert severity="warning">
                {preview.processedMyob
                    ? 'The full original purchase receipt will be returned in MYOB. Enter any current stock that must remain after the return.'
                    : 'This receival has not been pushed to MYOB. All of its stock will be removed and the purchase order will be cancelled.'}
            </Alert>

            {preview.canKeepStock && preview.stock.length > 0 ? (
                <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>Stock to keep</Typography>
                    <Stack spacing={1.5}>
                        {preview.stock.map((item, index) => (
                            <Box key={`${item.stockItemId}-${item.warehouseId}-${item.lotSerialNbr}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 150px' }, gap: 1.5, alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="body1">{item.stockItem} at {item.warehouse}</Typography>
                                    <Typography variant="body2" color="text.secondary">{item.lotSerialNbr} · {item.availableQuantity} dozen available</Typography>
                                </Box>
                                <TextField
                                    label="Quantity to keep"
                                    type="number"
                                    size="small"
                                    value={item.quantityToKeep}
                                    onChange={event => updateQuantityToKeep(index, event.target.value)}
                                    inputProps={{ min: 0, max: item.availableQuantity, step: 0.01 }}
                                />
                            </Box>
                        ))}
                    </Stack>
                </Box>
            ) : null}

            <TextField
                required
                fullWidth
                multiline
                minRows={3}
                label="Reason for rejection"
                value={reason}
                onChange={event => setReason(event.target.value)}
                inputProps={{ maxLength: 1000 }}
            />

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                <Button variant="contained" color="error" startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <BlockIcon />} onClick={rejectReceival} disabled={submitting}>
                    Reject receival
                </Button>
            </Stack>
        </Stack>
    );
};

export default RejectReceivalForm;